import { Business } from '@app/domain/Business'
import { Booking } from '@app/domain/Booking'
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { Customer } from '@app/domain/Customer'
import { CustomerService } from '@services/customer.service'
import { AbstractControl, FormGroup, Validators } from '@angular/forms'
import { Organisation } from '@app/domain/Organisation'
import { Venue } from '@app/domain/Venue'
import {
    combineLatest,
    debounceTime, filter,
    merge,
    switchMap,
    takeUntil,
} from 'rxjs'
import { startWith } from 'rxjs/operators'
import { CustomerComponent } from '../customer/customer.component'
import { NgbModal, NgbOffcanvas, NgbOffcanvasOptions } from '@ng-bootstrap/ng-bootstrap'
import {
    LoadBookingComponent,
} from '@app/features/shared/components/load-booking/load-booking.component'

@Component({
    selector: 'app-contact-form',
    templateUrl: './contact-form.component.html',
})
export class ContactFormComponent implements OnInit, OnChanges, OnDestroy {

    @Input() organisation!: Organisation
    @Input() business!: Business
    @Input() venue!: Venue
    @Input() form!: FormGroup
    @Input() formNameTitle: string | null = 'Customer Details'
    @Input() formContactDetailsTitle: string | null = 'Contact Information'
    @Input() offerCustomerSuggestions = true
    @Input() canClickCustomerDetails = false
    customerDetailsSubject = new EventEmitter<{
        firstName: string,
        lastName: string,
        emailAddress: string,
        phoneNumber: string
    }>()
    customerSearch: Customer[] = []
    focusedInput: 'firstName' | 'lastName' | 'emailAddress' | 'phoneNumber' | null = null
    showCustomerSuggestions = false
    loseFocusTimeoutId?: any
    canClearDetails = false
    selectedCustomer: Customer | null = null
    placeholders = {
        firstName: 'First Name',
        lastName: 'Last Name',
        emailAddress: 'Email Address',
        phoneNumber: 'Phone Number',
    }
    private minimumSearchLength = 3
    private onDestroy = new EventEmitter()
    private formRebind = new EventEmitter()

    constructor(
        private customerService: CustomerService,
        private modalService: NgbModal,
        private offcanvasService: NgbOffcanvas
    ) { }

    ngOnInit() {
        this.bindContactFieldsToCanClearDetails()
        this.bindFormInputToClearingSelectedCustomerDetails()
        this.bindPlaceholdersToOptionality()
        if (!this.offerCustomerSuggestions) {
            this.showCustomerSuggestions = false
            return
        }
        this.bindContactFieldsToGettingSuggestions()
        this.bindContactSearchToSearch()
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['form']) {
            this.formRebind.emit()
            if (this.form) {
                this.bindPlaceholdersToOptionality()
            }
        }
    }

    ngOnDestroy() {
        this.onDestroy.emit()
        this.onDestroy.complete()
    }

    clearSelected() {
        this.form.patchValue({
            firstName: null,
            lastName: null,
            emailAddress: null,
            phoneNumber: null,
        })
        this.form.get('cardDetailsNotRequired')?.setValue(false)
        this.selectedCustomer = null
    }

    customerSuggestionSelected(customer: Customer) {
        this.form.patchValue({
            firstName: customer.firstName,
            lastName: customer.lastName,
            emailAddress: customer.emailAddress,
            phoneNumber: customer.phoneNumber,
        })
        this.form.get('cardDetailsNotRequired')?.setValue(customer.cardDetailsNotRequired)
        this.selectedCustomer = customer
    }

    customerDetailsClicked(customer: Customer) {
        const customerModal = this.modalService.open(
            CustomerComponent,
            {
                size: 'lg',
            }
        )
        const customerComponent = customerModal.componentInstance as CustomerComponent
        customerComponent.customer = customer
        customerComponent.customerChanged
        .pipe(takeUntil(this.onDestroy))
        .subscribe(customer => {
            this.customerSuggestionSelected(customer)
        })
        customerComponent.bookingSelected
            .pipe(takeUntil(this.onDestroy))
            .subscribe(booking => {
                customerModal.close()
                this.openBookingOffcanvas(booking)
            })
    }

    private openBookingOffcanvas(booking: Booking) {
        let isEditingBooking = false
        const options: NgbOffcanvasOptions = {
            position: 'end',
            panelClass: 'booking-offcanvas-panel',
            beforeDismiss: () => !isEditingBooking,
        }
        const panel = this.offcanvasService.open(LoadBookingComponent, options)
        const component = panel.componentInstance as LoadBookingComponent
        component.organisation = this.organisation
        component.business = this.business
        component.venue = this.venue
        component.booking = booking
        component.bookingBeingChanged
            .pipe(takeUntil(this.onDestroy))
            .subscribe(isBeingChanged => {
                isEditingBooking = isBeingChanged
            })
    }

    focusInFirstName() {
        if (!this.offerCustomerSuggestions) {
            this.focusedInput = 'firstName'
            this.showCustomerSuggestions = false
            return
        }
        this.focusedInput = 'firstName'
        this.showCustomerSuggestions = true
        clearTimeout(this.loseFocusTimeoutId)
    }

    focusOutFirstName() {
        this.inputHasLostFocus()
    }

    focusInLastName() {
        if (!this.offerCustomerSuggestions) {
            this.focusedInput = 'lastName'
            this.showCustomerSuggestions = false
            return
        }
        this.focusedInput = 'lastName'
        this.showCustomerSuggestions = true
        clearTimeout(this.loseFocusTimeoutId)
    }

    focusOutLastName() {
        this.inputHasLostFocus()
    }

    focusInEmailAddress() {
        if (!this.offerCustomerSuggestions) {
            this.focusedInput = 'emailAddress'
            this.showCustomerSuggestions = false
            return
        }
        this.focusedInput = 'emailAddress'
        this.showCustomerSuggestions = true
        clearTimeout(this.loseFocusTimeoutId)
    }

    focusOutEmailAddress() {
        this.inputHasLostFocus()
    }

    focusInPhoneNumber() {
        if (!this.offerCustomerSuggestions) {
            this.focusedInput = 'phoneNumber'
            this.showCustomerSuggestions = false
            return
        }
        this.focusedInput = 'phoneNumber'
        this.showCustomerSuggestions = true
        clearTimeout(this.loseFocusTimeoutId)
    }

    focusOutPhoneNumber() {
        this.inputHasLostFocus()
    }

    hideCustomerSuggestions() {
        this.showCustomerSuggestions = false
    }

    private isRequired(control: AbstractControl | null): boolean {
        if (!control) {
            return false
        }
        return control.hasValidator(Validators.required)
            || control.hasValidator(Validators.requiredTrue)
    }

    private updatePlaceholders() {
        this.placeholders = {
            firstName: this.isRequired(this.form?.get('firstName') ?? null)
                ? 'First Name'
                : 'First Name (optional)',
            lastName: this.isRequired(this.form?.get('lastName') ?? null)
                ? 'Last Name'
                : 'Last Name (optional)',
            emailAddress: this.isRequired(this.form?.get('emailAddress') ?? null)
                ? 'Email Address'
                : 'Email Address (optional)',
            phoneNumber: this.isRequired(this.form?.get('phoneNumber') ?? null)
                ? 'Phone Number'
                : 'Phone Number (optional)',
        }
    }

    private bindPlaceholdersToOptionality() {
        if (!this.form) {
            return
        }
        const controls = [
            this.form.get('firstName'),
            this.form.get('lastName'),
            this.form.get('emailAddress'),
            this.form.get('phoneNumber'),
        ]
        if (controls.some(control => !control)) {
            return
        }
        combineLatest(
            controls.map(control => control!.statusChanges.pipe(startWith(control!.status)))
        )
            .pipe(takeUntil(merge(this.onDestroy, this.formRebind)))
            .subscribe(() => {
                this.updatePlaceholders()
            })
    }

    private inputHasLostFocus() {
        this.loseFocusTimeoutId = setTimeout(() => {
            this.focusedInput = null
            this.showCustomerSuggestions = false
        }, 200)
    }

    private bindContactFieldsToCanClearDetails() {
        combineLatest([
            this.form.get('firstName')!.valueChanges.pipe(startWith('')),
            this.form.get('lastName')!.valueChanges.pipe(startWith('')),
            this.form.get('emailAddress')!.valueChanges.pipe(startWith('')),
            this.form.get('phoneNumber')!.valueChanges.pipe(startWith('')),
        ])
            .pipe(takeUntil(this.onDestroy))
            .subscribe((fields) => {
                this.canClearDetails = fields.some(field => field && field.trim().length > 0)
            })
    }

    private bindContactFieldsToGettingSuggestions() {
        if (!this.offerCustomerSuggestions) {
            return
        }
        combineLatest([
            this.form.get('firstName')!.valueChanges.pipe(startWith('')),
            this.form.get('lastName')!.valueChanges.pipe(startWith('')),
            this.form.get('emailAddress')!.valueChanges.pipe(startWith('')),
            this.form.get('phoneNumber')!.valueChanges.pipe(startWith('')),
        ])
            .pipe(takeUntil(this.onDestroy))
            .subscribe((fields) => {
                this.customerDetailsSubject.next({
                    firstName: fields[0]?.trim(),
                    lastName: fields[1]?.trim(),
                    emailAddress: fields[2]?.trim(),
                    phoneNumber: fields[3]?.trim(),
                })
            })
    }

    private bindContactSearchToSearch() {
        if (!this.offerCustomerSuggestions) {
            return
        }
        this.customerDetailsSubject
            .pipe(
                takeUntil(this.onDestroy),
                debounceTime(300),
                filter((value) => {
                    const meetsMinimum = value.firstName?.length >= this.minimumSearchLength
                        || value.lastName?.length >= this.minimumSearchLength
                        || value.emailAddress?.length >= this.minimumSearchLength
                        || value.phoneNumber?.length >= this.minimumSearchLength
                    if (!meetsMinimum) {
                        this.customerSearch = []
                        this.showCustomerSuggestions = false
                    }
                    return meetsMinimum
                }),
                switchMap((value) => {
                    return this.customerService.searchCustomers(
                        this.organisation.id,
                        this.business.id,
                        this.venue.id,
                        value.firstName === '' ? null : value.firstName,
                        value.lastName === '' ? null : value.lastName,
                        value.emailAddress === '' ? null : value.emailAddress,
                        value.phoneNumber === '' ? null : value.phoneNumber
                    )
                })
            )
            .subscribe({
                next: (customers) => {
                    this.customerSearch = customers
                    if (customers.length > 0) {
                        if (this.focusedInput === null) {
                            this.showCustomerSuggestions = false
                        }
                        this.showCustomerSuggestions = true
                    }
                },
            })
    }

    private bindFormInputToClearingSelectedCustomerDetails() {
        // We're not linking directly to the customer when selecting a suggestion
        // the backend decides which customer to link to when creating the booking
        // based on the contact details.
        // If the user changes the form, we should clear the selected details
        // as we cannot guarantee it is still relevant
        merge(
            this.form.get('firstName')!.valueChanges,
            this.form.get('lastName')!.valueChanges,
            this.form.get('emailAddress')!.valueChanges,
            this.form.get('phoneNumber')!.valueChanges,
        )
            .pipe(takeUntil(this.onDestroy))
            .subscribe(() => {
                this.selectedCustomer = null
                this.form.get('cardDetailsNotRequired')?.setValue(false)
            })
    }
}
